
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AdminFeedback {
  id: string;
  user_id: string;
  content: string;
  sentiment_score: number;
  status: string;
  created_at: string;
  updated_at: string;
  user: {
    username: string;
    main_photo_url: string | null;
  };
}

export interface AdminFeedbacksData {
  feedbacks: AdminFeedback[];
  stats: {
    total: number;
    positive: number;
    negative: number;
    neutral: number;
    avgScore: number;
  };
  loading: boolean;
  error: Error | null;
}

export const useAdminFeedbacks = (): AdminFeedbacksData => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-feedbacks'],
    queryFn: async () => {
      // First, fetch all feedbacks
      const { data: feedbacksData, error: feedbacksError } = await supabase
        .from('feedbacks')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (feedbacksError) {
        throw feedbacksError;
      }

      if (!feedbacksData || feedbacksData.length === 0) {
        return {
          feedbacks: [],
          stats: {
            total: 0,
            positive: 0,
            negative: 0,
            neutral: 0,
            avgScore: 0
          }
        };
      }

      // Extract unique user IDs
      const userIds = [...new Set(feedbacksData.map(feedback => feedback.user_id))];

      // Fetch profiles for these users
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, username, main_photo_url')
        .in('id', userIds);

      if (profilesError) {
        throw profilesError;
      }

      // Create a map of user profiles for quick lookup
      const profilesMap = new Map();
      profilesData?.forEach(profile => {
        profilesMap.set(profile.id, profile);
      });

      // Transform data to match expected format
      const feedbacks: AdminFeedback[] = feedbacksData.map(feedback => {
        const userProfile = profilesMap.get(feedback.user_id);
        
        return {
          id: feedback.id,
          user_id: feedback.user_id,
          content: feedback.content,
          sentiment_score: feedback.sentiment_score,
          status: feedback.status,
          created_at: feedback.created_at,
          updated_at: feedback.updated_at,
          user: {
            username: userProfile?.username || 'Usuário Anônimo',
            main_photo_url: userProfile?.main_photo_url || null
          }
        };
      });

      // Calculate statistics
      const total = feedbacks.length;
      const positive = feedbacks.filter(f => f.sentiment_score >= 7).length;
      const negative = feedbacks.filter(f => f.sentiment_score < 4).length;
      const neutral = feedbacks.filter(f => f.sentiment_score >= 4 && f.sentiment_score < 7).length;
      const avgScore = total > 0 ? feedbacks.reduce((sum, f) => sum + f.sentiment_score, 0) / total : 0;

      return {
        feedbacks,
        stats: {
          total,
          positive,
          negative,
          neutral,
          avgScore: Math.round(avgScore * 10) / 10
        }
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    feedbacks: data?.feedbacks || [],
    stats: data?.stats || { total: 0, positive: 0, negative: 0, neutral: 0, avgScore: 0 },
    loading: isLoading,
    error: error as Error | null
  };
};
